import { Component, Input, Output, EventEmitter, SimpleChanges, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { InteractivityEditorService } from '../interactivity-editor/services/interactivity-editor.service';
import { bbDeepCopy } from '../../../shared/utils/bbDeepCopy.util';
import { BbMacrosService } from '../../../services/bb-macros.service';
import { SapiService } from '../../../services/sapi.service';
import { InteractivityActionService } from '../interactivity-editor/services/interactivity-action.service';
import { Subscription } from 'rxjs';

export interface BbDataSource {
	defaultValue?: any;
	sourceType?: string;
	sourceValue?: any;
}

@Component({
	selector: 'bb-data-source-picker',
	templateUrl: './bb-data-source-picker.component.html',
	styleUrls: ['./bb-data-source-picker.component.scss']
})

export class BbDataSourcePickerComponent implements OnInit, OnDestroy, OnChanges {

	@Input() items: { label: string; value: any }[];
	@Input() extraItems: { label: string; value: any }[];
	@Input() model: any;
	@Input() valueProperty: any = 'value';
	@Input() separateDataSource = false;
	@Input() disallow: any = {};
	@Output() onOpened: EventEmitter<any> = new EventEmitter<any>();
	@Output() closed: EventEmitter<any> = new EventEmitter<any>();
	@Output() ok: EventEmitter<any> = new EventEmitter<any>();
	@Output() cancel: EventEmitter<any> = new EventEmitter<any>();
	@Output() remove: EventEmitter<any> = new EventEmitter<any>();

	radioOptions = [
		{
			label: 'Local storage value',
			icon: '',
			value: 'LocalStorage'
		},
		{
			label: 'Datafeed',
			icon: '',
			value: 'DatafeedValue'
		},
		{
			label: 'Query string',
			icon: '',
			value: 'QueryString'
		},
		{
			label: 'Javascript',
			icon: '',
			value: 'JavaScript'
		},
		{
			label: 'Text with macro',
			icon: '',
			value: 'PlainText'
		},
		{
			label: 'Widget property',
			icon: '',
			value: 'WidgetProperty'
		},
	];

	datasourceObj: BbDataSource;
	selectionOptions: any = [];
	selectionModel: string;
	fieldType = 'text';
	localstorageKeyOptions: any = [];
	macroOptions: any[] = [];
	macroSelector = '';

	fieldLabel = 'VALUE';
	fieldLabel2 = 'VALUE';
	widgetModel: string;
	widgetProperty: string;
	widgetPropertyOptions: any[] = [];
	previousSourceType = '';

	datafeedValueOptions: any[] = [];
	datafeedValue: string;
	datafeedId: any;

	private subscriptions = new Subscription();

	constructor(
		private interactivityEditorService: InteractivityEditorService,
		private actionService: InteractivityActionService,
		private sapiService: SapiService,
		private bbMacrosService: BbMacrosService) {
	}

	ngOnInit() {
		this.init();

		this.localstorageKeyOptions = this.interactivityEditorService.localstorageKeys.map(v => ({ label: v, value: v }));
	}

	ngOnDestroy() {
		this.closed.emit();
		this.subscriptions.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.model && !changes.model.firstChange) {
			this.init();
		}
	}

	init() {
		this.radioOptions = this.radioOptions.filter((opt) => !this.disallow[opt.value]);
		this.subscriptions.add(this.bbMacrosService.macroSubject.subscribe((macroOptions) => {
			this.macroOptions = macroOptions;
		}));

		this.model = bbDeepCopy(this.model);
		if (!this.separateDataSource) {
			if (this.model && (!this.model[this.valueProperty] || typeof this.model[this.valueProperty] === 'string' ||
				typeof this.model[this.valueProperty] === 'number' || typeof this.model[this.valueProperty] === 'boolean')) {
				this.model[this.valueProperty] = {
					datasource: {
						defaultValue: this.model[this.valueProperty] || '',
						sourceType: this.radioOptions[0].value,
						sourceValue: ''
					}
				};
			}
			this.datasourceObj = this.model[this.valueProperty].datasource;
		} else {
			this.valueProperty = 'datasource';
			if (!this.model.datasource) {
				this.model.datasource = {
					sourceType: this.radioOptions[0].value,
					sourceValue: ''
				};
			}
			this.datasourceObj = this.model.datasource;
		}
		if (this.datasourceObj &&
			(this.datasourceObj.sourceType === 'WidgetProperty' || this.datasourceObj.sourceType === 'DatafeedValue') &&
			this.datasourceObj.sourceValue && this.datasourceObj.sourceValue.indexOf(':') !== -1) {
			const parts = this.datasourceObj.sourceValue.split(':');
			if (this.datasourceObj.sourceType === 'WidgetProperty') {
				this.widgetModel = parts[0] || '';
				this.widgetProperty = parts[1] || '';
			}
			if (this.datasourceObj.sourceType === 'DatafeedValue') {
				this.datafeedId = parts[0] || '';
				this.datafeedValue = parts[1] || '';
			}
		}
		this.onRadioChange();
		this.checkWidgetPropertyOptions();
		this.getDatafeedValues();
	}

	checkWidgetPropertyOptions() {
		if (this.widgetModel) {
			const type = this.interactivityEditorService.getWidgetTypeForId(this.widgetModel);
			if (type) {
				this.widgetPropertyOptions = this.actionService.getWidgetPropOptionsForType(type);
			}
		}
	}

	onValueChange(val: any) {
		this.datafeedValue = null;
		this.checkWidgetPropertyOptions();
		this.checkAndUpdateCombinedSourceValue();
		this.getDatafeedValues();
	}

	checkAndUpdateCombinedSourceValue() {
		if (this.datasourceObj) {
			if (this.datasourceObj.sourceType === 'WidgetProperty') {
				this.datasourceObj.sourceValue = (this.widgetModel || '') + ':' + (this.widgetProperty || '');
			}
			if (this.datasourceObj.sourceType === 'DatafeedValue') {
				this.datasourceObj.sourceValue = (this.datafeedId || '') + ':' + (this.datafeedValue || '');
			}
		}
	}

	onWidgetPropertyChange(val: any) {
		this.checkAndUpdateCombinedSourceValue();
	}

	onDatafeedValueSelected() {
		this.checkAndUpdateCombinedSourceValue();
	}

	getDatafeedValues() {
		this.datafeedValueOptions = [];
		if (this.datafeedId) {
			this.sapiService.get('datafeed', this.datafeedId).subscribe((dm: any) => {
				if (dm && dm.columns && dm.columns.length) {
					this.datafeedValueOptions = dm.columns
						.filter((col: any) => col && col.headerType === 'value')
						.map((col: any) => ({
							label: col.label || col.key,
							value: col.key
						}));
				}
			});
		}
	}

	onRadioChange() {
		if (this.previousSourceType && this.datasourceObj.sourceType !== this.previousSourceType) {
			this.datasourceObj.sourceValue = '';
		}
		switch (this.datasourceObj.sourceType) {
			default:
				this.fieldType = 'text';

				break;

			case 'LocalStorage':
				this.fieldLabel = 'LOCALSTORAGE_KEY';
				this.fieldType = 'entitySelector';
				this.selectionOptions = this.localstorageKeyOptions;
				break;

			case 'DatafeedValue':
				this.fieldLabel = 'DATAFEED';
				this.fieldLabel2 = 'VALUE';
				this.fieldType = 'datafeedSelector';
				this.getDatafeedValues();
				break;

			case 'QueryString':
				this.fieldLabel = 'PARAMETER';
				this.fieldType = 'text';
				break;

			case 'PlainText':
				this.fieldLabel = 'TEXT';
				this.fieldType = 'text';
				this.selectionOptions = this.macroOptions;
				break;

			case 'WidgetProperty':
				this.fieldLabel = 'WIDGET';
				this.fieldLabel2 = 'PROPERTY';
				this.fieldType = 'widgetSelector';
				this.selectionOptions = this.interactivityEditorService.widgetsAsOptions;
				break;

			case 'JavaScript':
				this.fieldLabel = 'SCRIPT';
				this.fieldType = 'scriptEditor';
				break;

			case '':
				break;
		}
		if (this.datasourceObj.sourceType) {
			this.previousSourceType = this.datasourceObj.sourceType;
		}
	}

	onMacroSelected(selectedOption: any) {
		this.datasourceObj.sourceValue += selectedOption.value;
		setTimeout(() => {
			this.macroSelector = '';
		});
	}

	onRemoveClick() {
		this.remove.emit(this.model);
	}

	onCancelClick() {
		this.cancel.emit(this.model);
	}

	onOkClick() {
		this.ok.emit(this.model);
	}
}
